/**
 * CostSummaryPage Component
 * 
 * A decision surface for cost performance - not just a dashboard.
 * Implements the MIPS_Cost_Dashboard_Overview_Page_Specification_v1.0
 * 
 * Design Principles:
 * - Earn attention in 10 seconds (Summary Bar)
 * - Build trust in 30 seconds (Benchmarks with context)
 * - Enable action in 2 minutes (Cohort recommendations)
 * - Withstand clinical scrutiny (Risk-adjusted visualization)
 */

import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  DollarSign,
  Activity,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  Target,
  Heart,
  Stethoscope,
  Pill,
  Brain,
  Home,
  Ambulance,
  RefreshCw,
} from 'lucide-react';
import { getCostDashboardData, formatCurrency, formatCurrencyFull, type CostMeasure, type CostStrategy } from '@/lib/cost-calculator-utils';
import { cn } from '@/lib/utils';
import { useProgramYear } from '@/contexts/ProgramYearContext';

interface CostSummaryPageProps {
  practiceId: string;
  evaluationType: 'GROUP' | 'NPI';
  selectedNpi?: string;
  onNavigateToPatients?: (strategyId: string) => void;
  onNavigateToStrategy?: (strategyId: string, highCostFilter?: boolean) => void;
  onNavigateToMeasure?: (measureId: string) => void;
}

interface PortfolioData {
  uniquePatients: number;
  totalSavings: number;
  highCostTotalSavings?: number; // Savings for high-cost patients only
  savingsByStrategy: Record<string, number>;
  cohortTotalCost: number;
  portfolioTotalCost: number;
  percentOfCost: string;
  programCount: number;
  // High-cost analysis (cost-based Pareto)
  highCostAnalysis?: {
    targetPercent: number;
    patientCount: number;
    totalAlivePatients: number;
    percentOfPopulation: number;
    cohortTotalCost: number;
    totalCost: number;
    percentOfCost: number;
    costThreshold: number;
    avgCostInCohort: number;
    avgCostOutsideCohort: number;
  };
  // Risk flags
  riskFlags?: {
    readmitWatch: {
      patientCount: number;
      highCostPatientCount?: number;
      inCareManagement: number;
      notInCareManagement: number;
    };
  };
  // Risk flags summary from backend
  riskFlagsSummary?: Array<{
    id: string;
    name: string;
    patientCount: number;
    highCostPatientCount?: number;
    inCareManagement: number;
    notInCareManagement: number;
  }>;
  // Strategies with CDM tiers - now includes highCostCandidateCount
  strategies?: Array<{
    id: string;
    name: string;
    candidateCount: number;
    highCostCandidateCount?: number;
    expectedSavings: number;
    highCostSavings?: number;
    tiers?: Array<{
      tier: number;
      name: string;
      candidateCount: number;
      highCostCandidateCount?: number;
      potentialSavings: number;
    }>;
  }>;
  // Legacy compatibility
  paretoAnalysis?: {
    top20PatientCount: number;
    totalAlivePatients: number;
    deceasedPatients: number;
    top20PercentOfCost: number;
    top20TotalCost: number;
    totalAliveCost: number;
  };
}

interface RiskCalibration {
  calibrated: boolean;
  calibratedAt?: string;
  warning?: string;
  tiers: Array<{
    percentileMin: number;
    percentileMax: number;
    multiplier: number;
    tierAvgCost: number | null;
    sampleSize: number | null;
  }>;
}

// Strategy icons and colors (Updated per Strategy Redesign Spec v1.1)
const STRATEGY_CONFIG: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  cdm: { icon: Heart, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  pcm: { icon: Activity, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  snf: { icon: Home, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  ed: { icon: Ambulance, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  medopt: { icon: Pill, color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
  bh: { icon: Brain, color: 'text-pink-600', bgColor: 'bg-pink-50' },
  readmit_watch: { icon: AlertTriangle, color: 'text-red-600', bgColor: 'bg-red-50' },
};

const STRATEGY_NAMES: Record<string, string> = {
  cdm: 'Chronic Disease Management',
  pcm: 'PCM Enrollment',
  snf: 'SNF Optimization',
  ed: 'ED Diversion',
  medopt: 'Medication Optimization',
  bh: 'Behavioral Health Integration',
  readmit_watch: 'Readmissions Watch',
};

const STRATEGY_DESCRIPTIONS: Record<string, string> = {
  cdm: 'Tiered care coordination (CCM + RPM)',
  pcm: 'High-cost single-condition patients',
  snf: 'Shift SNF to home health when appropriate',
  ed: 'Redirect non-emergent ED visits',
  medopt: 'Med review for diabetes/polypharmacy',
  bh: 'Integrated behavioral health services',
  readmit_watch: 'Priority for care management enrollment',
};

type CohortViewMode = 'highCost' | 'interventionCandidates';

export function CostSummaryPage({
  practiceId,
  evaluationType,
  selectedNpi,
  onNavigateToPatients,
  onNavigateToStrategy,
  onNavigateToMeasure,
}: CostSummaryPageProps) {
  // Viewing year — cost endpoints must be year-scoped (fix: was omitted, so
  // the backend fell back to the operating year regardless of the selector)
  const { programYear } = useProgramYear();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [measures, setMeasures] = useState<CostMeasure[]>([]);
  const [strategies, setStrategies] = useState<CostStrategy[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioData | null>(null);
  const [calibration, setCalibration] = useState<RiskCalibration | null>(null);
  const [categoryScore, setCategoryScore] = useState<number>(0);
  const [totalOpportunity, setTotalOpportunity] = useState<number>(0);
  
  // Toggle state: show high-cost patients (default) or all intervention candidates
  const [cohortViewMode, setCohortViewMode] = useState<CohortViewMode>('highCost');
  
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const token = localStorage.getItem('auth_token');

  // Load all data
  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      setError(null);
      
      try {
        const npiParam = evaluationType === 'NPI' && selectedNpi ? `&npi=${selectedNpi}` : '';
        const headers = { 'Authorization': `Bearer ${token}` };
        
        // Fetch all data in parallel — every URL carries the viewing year
        console.log('[CostSummaryPage] Loading data for practice:', practiceId, 'year:', programYear, 'apiUrl:', apiUrl);
        const query = `?year=${programYear}${npiParam}`;
        const [dashboardRes, portfolioRes, calibrationRes, strategiesRes] = await Promise.all([
          fetch(`${apiUrl}/api/practices/${practiceId}/cost/dashboard${query}`, { headers }),
          fetch(`${apiUrl}/api/practices/${practiceId}/cost/portfolio${query}`, { headers }),
          fetch(`${apiUrl}/api/practices/${practiceId}/cost/calibration?year=${programYear}`, { headers }),
          fetch(`${apiUrl}/api/practices/${practiceId}/cost/strategies${query}`, { headers }),
        ]);
        
        console.log('[CostSummaryPage] Response statuses:', {
          dashboard: dashboardRes.status,
          portfolio: portfolioRes.status,
          calibration: calibrationRes.status,
          strategies: strategiesRes.status
        });
        
        if (!dashboardRes.ok) {
          const errorBody = await dashboardRes.text();
          console.error('[CostSummaryPage] Dashboard API error:', dashboardRes.status, errorBody);
          throw new Error(`Failed to load dashboard data: ${dashboardRes.status} - ${errorBody}`);
        }
        
        const dashboardData = await dashboardRes.json();
        setMeasures(dashboardData.measures || []);
        setCategoryScore(dashboardData.summary?.categoryScore || 0);
        setTotalOpportunity(dashboardData.summary?.totalOpportunity || 0);
        
        
        if (portfolioRes.ok) {
          setPortfolio(await portfolioRes.json());
        }
        
        if (calibrationRes.ok) {
          setCalibration(await calibrationRes.json());
        }
        
        if (strategiesRes.ok) {
          const stratData = await strategiesRes.json();
          setStrategies(stratData.strategies || []);
        }
        
      } catch (err) {
        console.error('Error loading summary data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    }
    
    loadData();
  }, [practiceId, evaluationType, selectedNpi, apiUrl, token, programYear]);


  // Achievement points earned as a share of the maximum (NOT a percentile rank)
  const scorePercentOfMax = useMemo(() => {
    return Math.round(categoryScore);
  }, [categoryScore]);

  // Performance status
  const performanceStatus = useMemo(() => {
    if (categoryScore >= 75) return { label: 'Excellent', color: 'text-emerald-600', bgColor: 'bg-emerald-50' };
    if (categoryScore >= 50) return { label: 'Above Benchmark', color: 'text-blue-600', bgColor: 'bg-blue-50' };
    if (categoryScore >= 30) return { label: 'Below Benchmark', color: 'text-amber-600', bgColor: 'bg-amber-50' };
    return { label: 'Needs Attention', color: 'text-red-600', bgColor: 'bg-red-50' };
  }, [categoryScore]);

  // Build interventions list based on toggle mode
  // Combines strategies + readmissions watch - ALWAYS show all, even with 0 count
  const interventionsList = useMemo(() => {
    const list: Array<{
      id: string;
      name: string;
      count: number;
      isRiskFlag?: boolean;
    }> = [];
    
    // Add strategies from portfolio (has both counts) - show all regardless of count
    if (portfolio?.strategies) {
      portfolio.strategies.forEach(strategy => {
        const count = cohortViewMode === 'highCost' 
          ? (strategy.highCostCandidateCount ?? strategy.candidateCount)
          : strategy.candidateCount;
        
        list.push({
          id: strategy.id,
          name: STRATEGY_NAMES[strategy.id] || strategy.name,
          count: count || 0,
        });
      });
    }
    
    // Always add Readmissions Watch - show even if 0 patients
    if (portfolio?.riskFlagsSummary) {
      const readmitFlag = portfolio.riskFlagsSummary.find(f => f.id === 'readmit_watch');
      const count = readmitFlag 
        ? (cohortViewMode === 'highCost'
            ? (readmitFlag.highCostPatientCount ?? readmitFlag.patientCount)
            : readmitFlag.patientCount)
        : 0;
      
      list.push({
        id: 'readmit_watch',
        name: 'Readmissions Watch',
        count: count || 0,
        isRiskFlag: true,
      });
    } else if (portfolio?.riskFlags?.readmitWatch) {
      // Fallback to legacy riskFlags structure
      const readmit = portfolio.riskFlags.readmitWatch;
      const count = cohortViewMode === 'highCost'
        ? (readmit.highCostPatientCount ?? readmit.patientCount)
        : readmit.patientCount;
      
      list.push({
        id: 'readmit_watch',
        name: 'Readmissions Watch',
        count: count || 0,
        isRiskFlag: true,
      });
    } else if (portfolio) {
      // Even if no risk flags data, always show Readmissions Watch
      list.push({
        id: 'readmit_watch',
        name: 'Readmissions Watch',
        count: 0,
        isRiskFlag: true,
      });
    }
    
    // Sort by count descending (but risk flags at the end)
    return list.sort((a, b) => {
      // Risk flags go to the end
      if (a.isRiskFlag && !b.isRiskFlag) return 1;
      if (!a.isRiskFlag && b.isRiskFlag) return -1;
      // Otherwise sort by count descending
      return b.count - a.count;
    });
  }, [portfolio, cohortViewMode]);

  // Calculate totals for current view
  const currentViewData = useMemo(() => {
    if (!portfolio) return { patientCount: 0, percentOfPanel: 0, avgCost: 0, costThreshold: 0 };
    
    if (cohortViewMode === 'highCost') {
      const hca = portfolio.highCostAnalysis;
      return {
        patientCount: hca?.patientCount || 0,
        percentOfPanel: hca?.percentOfPopulation || 0,
        percentOfCost: hca?.percentOfCost || 80,
        avgCost: hca?.avgCostInCohort || 0,
        costThreshold: hca?.costThreshold || 0,
      };
    } else {
      return {
        patientCount: portfolio.uniquePatients || 0,
        percentOfPanel: portfolio.paretoAnalysis?.totalAlivePatients 
          ? ((portfolio.uniquePatients || 0) / portfolio.paretoAnalysis.totalAlivePatients * 100)
          : 0,
        percentOfCost: parseFloat(portfolio.percentOfCost) || 0,
        avgCost: portfolio.uniquePatients > 0 
          ? (portfolio.cohortTotalCost / portfolio.uniquePatients)
          : 0,
        costThreshold: 0, // Not applicable for intervention candidates
      };
    }
  }, [portfolio, cohortViewMode]);

  // Benchmark chart data
  const benchmarkChartData = useMemo(() => {
    return measures
      .filter(m => m.scored)
      .map(m => ({
        measureId: m.measureId,
        name: m.name,
        yourValue: m.practiceAvgCost || m.avgCost,
        nationalAvg: m.nationalAvg,
        // Marker position on the 10-band track (decile × 10) — a chart position,
        // not a percentile rank
        bandPosition: Math.round((m.decileScore || 0) * 10),
        decile: m.decileScore,
        type: m.type,
      }))
      .sort((a, b) => (a.bandPosition || 0) - (b.bandPosition || 0));
  }, [measures]);

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-3 gap-6">
          <Skeleton className="h-64 col-span-2" />
          <Skeleton className="h-64" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-6">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6 p-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 min-h-full">
        {/* ============================================ */}
        {/* MAIN CONTENT AREA - 2 Column Layout */}
        {/* ============================================ */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column: Summary Metrics + Bullet Chart */}
          <div className="col-span-2 space-y-6">
            {/* 1. SUMMARY METRICS ROW - Only 3 cards */}
            <div className="grid grid-cols-3 gap-4">
              {/* Cost Performance */}
              <Card className="shadow-sm bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500 mb-2">Cost Performance</p>
                  <p className="text-2xl font-semibold text-gray-900">{categoryScore.toFixed(1)} <span className="text-base font-normal text-gray-400">/ 100</span></p>
                  <p className="text-xs text-gray-400 mt-1">{scorePercentOfMax}% of available points</p>
          </CardContent>
        </Card>

              {/* Unique Patients */}
              <Card className="shadow-sm bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500 mb-2">Unique Patients</p>
                  <p className="text-2xl font-semibold text-gray-900">{(portfolio?.paretoAnalysis?.totalAlivePatients || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">Across all cost measures</p>
              </CardContent>
            </Card>

              {/* Intervention Candidates */}
              <Card className="shadow-sm bg-white">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500 mb-2">Intervention Candidates</p>
                  <p className="text-2xl font-semibold text-gray-900">{portfolio?.uniquePatients || 0} <span className="text-base font-normal text-gray-500">patients</span></p>
                  <p className="text-xs text-gray-400 mt-1">= {portfolio?.percentOfCost || 0}% of spend (matched to programs)</p>
                </CardContent>
              </Card>
            </div>

            {/* 2. COST PERFORMANCE BULLET CHART - Clean, no status pills */}
            <Card className="shadow-md">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold">Cost Performance by Measure</CardTitle>
                <CardDescription>
                      Your position relative to national benchmarks
                </CardDescription>
                  </div>
                  {/* Legend */}
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-300"></div>
                      <span className="text-muted-foreground">0-50%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                      <span className="text-muted-foreground">50-75%</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                      <span className="text-muted-foreground">75-100%</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Header row */}
                <div className="grid grid-cols-12 gap-4 pb-2 border-b border-slate-200 text-xs font-medium text-muted-foreground">
                  <div className="col-span-2"></div>
                  <div className="col-span-6 flex justify-between px-1">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                  <div className="col-span-1 text-center">Decile</div>
                  <div className="col-span-1 text-right">Benchmark</div>
                  <div className="col-span-2 text-right">Your Cost</div>
                </div>
                
                {/* Measure rows */}
                <div className="divide-y divide-slate-100">
                  {benchmarkChartData.map((measure) => {
                    const variance = measure.nationalAvg > 0 
                      ? ((measure.yourValue) - measure.nationalAvg) / measure.nationalAvg * 100 
                      : 0;
                    
                    // Determine marker color based on variance
                    let markerColor = '#3b82f6'; // blue-500
                    if (variance > 20) {
                      markerColor = '#f97316'; // orange-500
                    } else if (variance > 10) {
                      markerColor = '#f59e0b'; // amber-500
                    } else if (variance < -5) {
                      markerColor = '#10b981'; // emerald-500
                    }
                    
                    const bandPosition = measure.bandPosition || 0;
                    
                    return (
                      <div 
                        key={measure.measureId}
                        className="grid grid-cols-12 gap-4 py-4 items-center hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => onNavigateToMeasure?.(measure.measureId)}
                      >
                        {/* Measure name */}
                        <div className="col-span-2 text-sm font-medium text-gray-900">
                          {measure.name}
                        </div>
                        
                        {/* Bullet chart - wide bars */}
                        <div className="col-span-6 relative h-6">
                          {/* Background bands */}
                          <div className="absolute inset-0 flex rounded overflow-hidden">
                            <div className="w-1/2 bg-gray-100"></div>
                            <div className="w-1/4 bg-gray-200"></div>
                            <div className="w-1/4 bg-gray-300"></div>
                          </div>
                          {/* Marker line */}
                          <div 
                            className="absolute top-0 bottom-0 w-1 rounded-full shadow-sm"
                            style={{ 
                              left: `${Math.min(Math.max(bandPosition, 0), 100)}%`,
                              backgroundColor: markerColor,
                              transform: 'translateX(-50%)'
                            }}
                          />
                        </div>
                        
                        {/* Decile Score */}
                        <div className="col-span-1 text-center">
                          <span className="text-sm font-semibold text-gray-700">
                            {measure.decile?.toFixed(1) || '—'}
                            </span>
                        </div>
                        
                        {/* Benchmark */}
                        <div className="col-span-1 text-right text-sm text-muted-foreground">
                          {formatCurrencyFull(measure.nationalAvg)}
                        </div>
                        
                        {/* Your cost with variance */}
                        <div className="col-span-2 text-right text-sm">
                          <span className="font-medium">{formatCurrencyFull(measure.yourValue)}</span>
                            <span className={cn(
                            'ml-1.5 text-xs',
                            variance > 0 ? 'text-red-500' : 'text-emerald-500'
                            )}>
                            {variance > 0 ? '+' : ''}{variance.toFixed(0)}%
                            </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Footer */}
                <div className="pt-3 mt-2 border-t border-slate-200">
                  <p className="text-xs text-muted-foreground">
                    Marker shows the measure's decile position (further right = better score) • Benchmark shown in gray, your cost in bold
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Right Column: Patient Cohort Card & Interventions */}
          <div className="space-y-4 sticky top-4">
            {/* Patient Cohort Card - Toggleable between High-Cost and All Intervention Candidates */}
            {portfolio && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Toggle Header */}
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-1 p-0.5 bg-slate-200 rounded-lg">
                    <button
                      onClick={() => setCohortViewMode('highCost')}
                      className={cn(
                        'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                        cohortViewMode === 'highCost'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      High-Cost Patients
                    </button>
                    <button
                      onClick={() => setCohortViewMode('interventionCandidates')}
                      className={cn(
                        'flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                        cohortViewMode === 'interventionCandidates'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      All Candidates
                    </button>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center gap-6">
                    {/* Circular indicator */}
                    <div className="relative w-28 h-28 flex-shrink-0">
                      <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          stroke="#E5E7EB"
                          strokeWidth="8"
                          fill="none"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="42"
                          className="stroke-primary"
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${Math.min(currentViewData.percentOfCost, 100) / 100 * 264} 264`}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold text-gray-900 leading-none">{currentViewData.patientCount}</span>
                        <span className="text-xs text-gray-400 mt-0.5">patients</span>
                  </div>
                </div>
                    {/* Text content */}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {cohortViewMode === 'highCost' ? 'High-Cost Patients' : 'Intervention Candidates'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {cohortViewMode === 'highCost' 
                          ? `${currentViewData.percentOfPanel.toFixed(0)}% of panel = ${currentViewData.percentOfCost.toFixed(0)}% of cost`
                          : `Matched to ${portfolio.programCount} programs`
                        }
                      </p>
                      {cohortViewMode === 'highCost' && currentViewData.avgCost > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Avg cost: {formatCurrency(currentViewData.avgCost)}
                      </p>
                    )}
                  </div>
                </div>
                </div>
                
                {/* Footer section - Always blue, always shows potential savings */}
                <div className="bg-primary px-6 py-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-white/80 uppercase tracking-wide">
                      Potential Savings
                    </span>
                    <span className="text-base font-bold text-white">
                      {cohortViewMode === 'highCost' 
                        ? formatCurrency(portfolio.highCostTotalSavings || 0)
                        : formatCurrency(portfolio.totalSavings)
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Recommended Interventions Card - Compact list style */}
            <Card>
              <CardContent className="p-0">
                {/* Header */}
                <div className="px-5 pt-4 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">Recommended Interventions</h3>
                    <span className="text-xs text-gray-400">
                      {cohortViewMode === 'highCost' ? 'High-cost only' : 'All candidates'}
                    </span>
                  </div>
          </div>

                {/* Compact list - shows based on toggle */}
                <div className="py-1">
                  {interventionsList.length > 0 ? (
                    interventionsList.map((intervention) => {
                      const config = STRATEGY_CONFIG[intervention.id] || { icon: Target, color: 'text-gray-600' };
                    const Icon = config.icon;
                    
                    return (
                      <div 
                          key={intervention.id}
                        className={cn(
                            "flex items-center gap-3 px-5 py-2.5 cursor-pointer transition-colors",
                            intervention.isRiskFlag 
                              ? "hover:bg-red-50/50 bg-red-50/30" 
                              : "hover:bg-gray-50"
                          )}
                          onClick={() => onNavigateToStrategy?.(intervention.id, cohortViewMode === 'highCost')}
                        >
                          <Icon className={cn('h-4 w-4 flex-shrink-0', config.color)} />
                          <span className="text-sm text-gray-700 flex-1 truncate">{intervention.name}</span>
                          <span className={cn(
                            "text-sm font-medium",
                            intervention.isRiskFlag ? "text-red-600" : "text-gray-500"
                          )}>
                            {intervention.count}
                              </span>
                          <ArrowRight className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" />
                      </div>
                    );
                  })
                ) : (
                    <div className="text-center py-6 text-muted-foreground">
                      <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No interventions available</p>
                  </div>
                )}
                </div>
                
                {/* Footer hint */}
                <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
                  <p className="text-xs text-gray-500">
                    Click an intervention to view eligible patients.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Card - Matches PI/IA */}
            <Card>
              <CardContent className="p-0">
                {/* Header with divider */}
                <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: '#F0F2F5' }}>
                  <h3 className="text-base font-semibold text-gray-900">Quick Actions</h3>
                </div>
                
                {/* Action rows */}
                <div className="px-3 py-2">
                  <button
                    onClick={() => {/* Placeholder - not wired */}}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  >
                    <Target className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">View All Interventions</span>
                  </button>
                  
                  <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
                  
                  <button
                    onClick={() => {/* Placeholder - not wired */}}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  >
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Review All Patients</span>
                  </button>
                  
                  <div className="mx-3 border-t" style={{ borderColor: '#F0F2F5' }} />
                  
                  <button
                    onClick={() => {/* Placeholder - not wired */}}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  >
                    <DollarSign className="w-5 h-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Review Top Patients Driving Costs</span>
                  </button>
                </div>
                
                {/* Footer with calibration status */}
                <div className="px-5 py-3 border-t bg-slate-50 rounded-b-lg" style={{ borderColor: '#E5E7EB' }}>
            {calibration && (
                  <div className="flex items-center gap-2">
                    {calibration.calibrated ? (
                      <>
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                          <span className="text-xs text-emerald-700">Risk multipliers calibrated</span>
                      </>
                    ) : (
                      <>
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-xs text-amber-700">Using estimated multipliers</span>
                      </>
                    )}
                  </div>
                  )}
                  {!calibration && (
                    <p className="text-xs text-gray-500">
                      Navigate to patient lists and programs.
                    </p>
                  )}
                </div>
                </CardContent>
              </Card>
                </div>
              </div>
      </div>
    </TooltipProvider>
  );
}
